/**
 * Analytics event batching.
 *
 * Queues events and flushes them in bulk instead of firing one request per event.
 * A flush happens when any of these triggers fire:
 *   - The timer expires (2 s)
 *   - The queue reaches 10 events
 *   - The payload would exceed 60 KB in bytes (sendBeacon's limit is 64 KB)
 *   - The page is hidden or unloaded
 *   - The caller invokes flushEvents() directly
 */

import { sendToEndpoint } from "./analyticsSend";

// ── Types ────────────────────────────────────────────────────────────

export interface EventData {
  app_name: string;
  event_name: string;
  properties: Record<string, unknown>;
  tenant_id: string;
  triggered_at: string;
}

// ── Config ───────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 2_000;
const MAX_QUEUE_SIZE = 10;
const MAX_PAYLOAD_BYTES = 60_000;

// ── State ────────────────────────────────────────────────────────────

let queue: EventData[] = [];
let queuePayloadSize = 0;
let flushTimerId: ReturnType<typeof setTimeout> | null = null;
let isUnloadListenerRegistered = false;

// ── Public API ───────────────────────────────────────────────────────

export const enqueueEvent = (event: EventData) => {
  registerUnloadListener();

  const eventSize = new Blob([JSON.stringify(event)]).size;

  if (queuePayloadSize + eventSize > MAX_PAYLOAD_BYTES) {
    flushEvents();
  }

  queue.push(event);
  queuePayloadSize += eventSize;

  if (queue.length >= MAX_QUEUE_SIZE) {
    flushEvents();
  } else {
    scheduleFlush();
  }
};

export const flushEvents = () => {
  cancelScheduledFlush();

  if (queue.length === 0) return;

  const batch = queue;
  queue = [];
  queuePayloadSize = 0;

  sendToServer(batch);
};

// ── Internals ────────────────────────────────────────────────────────

const scheduleFlush = () => {
  if (flushTimerId !== null) return;
  flushTimerId = setTimeout(flushEvents, FLUSH_INTERVAL_MS);
};

const cancelScheduledFlush = () => {
  if (flushTimerId === null) return;
  clearTimeout(flushTimerId);
  flushTimerId = null;
};

const registerUnloadListener = () => {
  if (isUnloadListenerRegistered) return;
  isUnloadListenerRegistered = true;

  window.addEventListener("beforeunload", flushEvents);
  window.addEventListener("pagehide", flushEvents);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushEvents();
    }
  });
};

const sendToServer = (events: EventData[]) => {
  const url = window?.ANALYTICS_TRACKER_URL;
  const token = window?.ANALYTICS_TRACKER_API_TOKEN;
  if (!url || !token) return;

  let body: string;
  try {
    body = JSON.stringify(events);
  } catch (error) {
    console.error("Error while converting batch data to JSON", error);
    return;
  }

  sendToEndpoint(url, token, body);
};
